import App from './application-container';
import HashTable from './hashtable';
import * as THREE from 'three';
import { WrappedValue } from '@phi/wrapped-value';
import * as m3 from './matrix3';
import * as v3 from './vector3';
import * as geom from './geometry';
import { pointer_normalised } from './pointer';
import { uil_get_point_at_edge_param, uil_is_joint_smooth } from './ui_logic';

// ----------------------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------------------
// ----------------------        D A T A   S T R U C T U R E S        ---------------------------------
// ----------------------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------------------

// Object to split 3D coordinates in hash -------------------------------------------------------------

export class Coordinate_triplets_hash {
  constructor (bbox, n_slots) {
    this.bbox = bbox;
    this.n_slots = n_slots;
    this.slot_hash = new HashTable();
  }

  get_slot_key_to_read_elements_for_point (v) {
    var keysx = this.find_slots(this.bbox.minx, this.bbox.maxx, this.n_slots, v.x);
    var keysy = this.find_slots(this.bbox.miny, this.bbox.maxy, this.n_slots, v.y);
    var keysz = this.find_slots(this.bbox.minz, this.bbox.maxz, this.n_slots, v.z);
    var k = keysx[0] + "_" + keysy[0] + "_" + keysz[0];
    return k;
  }

  get_slot_keys_to_add_elements_for_point (v) {
    var keysx = this.find_slots(this.bbox.minx, this.bbox.maxx, this.n_slots, v.x);
    var keysy = this.find_slots(this.bbox.miny, this.bbox.maxy, this.n_slots, v.y);
    var keysz = this.find_slots(this.bbox.minz, this.bbox.maxz, this.n_slots, v.z);
    var keys = [];
    keysx.forEach(function (keyx) {
      keysy.forEach(function (keyy) {
        keysz.forEach(function (keyz) {
          var k = keyx + "_" + keyy + "_" + keyz;
          keys.push(k);
        });
      });
    });
    return keys;
  }

  add_element_to_slot_hash_for_point(v, e) {
    var self = this;
    var slot_keys = this.get_slot_keys_to_add_elements_for_point(v);
    slot_keys.forEach( function (slot_key) {
      if (self.slot_hash.hasItem(slot_key)) {
        // Add if not already in
        var ar = self.slot_hash.getItem(slot_key);
        var found = false;
        ar.forEach(function(ce){
          if (self.elements_are_equal(ce,e))
            found = true;
        });
        if (!found)
          self.slot_hash.getItem(slot_key).push(e);
      }
      else
        self.slot_hash.setItem(slot_key, [e]);
    });
  }

  elements_are_equal(e1, e2) {
    return (e1.index1 == e2.index1 && e1.index2 == e2.index2 && e1.index3 == e2.index3)
  }

  get_slot_elements_for_point(v) {
    return this.get_slot_elements(this.get_slot_key_to_read_elements_for_point(v));
  }

  get_slot_elements(slot_key) {
    var ar = this.slot_hash.getItem(slot_key) || [];
    return ar;
  }

  get_slot_numbers_from_slot_key(slot_key) {
    var slots = slot_key.split("_");
    return slots;
  }

  get_slot_key_from_slot_numbers(nx, ny, nz) {
    return nx + "_" + ny + "_" + nz;
  }

  find_slots(min, max, n_slots, v) {
    if (v <= min)
      return [0];
    if (v >= max)
      return [(n_slots - 1)];
    var width = (max-min)/n_slots;
    var slots = [];
    var slot = Math.floor((v - min)/width);
    slots.push(slot);
    // Also consider neighbouring slots if we're near the border by 0.25
    if (slot > 0 && ( v - (min + slot * width) < width/4.0))
      slots.push(slot-1);
    if (slot < (n_slots - 1) && ( min + ( slot + 1) * width - v) < width/4.0)
      slots.push(slot+1);
    return slots;
  }
}


// Object to store tooltips ---------------------------------------------------------------------------

export class Shortcut {
  constructor (relevant_icon, code, character, brief_description, description) {
    this.relevant_icon = relevant_icon;
    this.code = code;
    this.character = character;
    this.brief_description = brief_description;
    this.description = description;
  }
}

export class Shortcuts {
  constructor () {
    this.shortcuts = new HashTable();
  }

  add_shortcut (relevant_icon, code, character, brief_description, description) {
    var t = new Shortcut(relevant_icon, code, character, brief_description, description);
    this.shortcuts.setItem(code, t);
  }

  create_shortcut_html (code) {
    var s = "";
    s = s + "<li class='shortcuts__item'><b>" + this.shortcuts.getItem(code).character + "</b> : " + this.shortcuts.getItem(code).brief_description + "</li>";
    return s;
  }

  create_shortcut_short_html (code) {
    var s = "";
    s = s + "<b>" + this.shortcuts.getItem(code).character + "</b>";
    return s;
  }

  get_keys () {
    return this.shortcuts.keys();
  }

  get_item (k) {
    return this.shortcuts.getItem(k);
  }

}

// Edge tangent manipulator -----------------------------------------------------------------------

// An edge_tangent_manipulator geometric description. Consists of:
// A center (Vector3)
// The tangent direction (Vector3)

export class Uid_edge_tangent_manipulator_object {
  constructor (edge_object, i) {
    // Initializes the manipulator positioned at (0,0,0) with tangent along (1,0,0).

    // Vector description of manipulator location and orientation
    this.center = new THREE.Vector3(0, 0, 0);
    this.local_x_direction = new THREE.Vector3(1, 0, 0);

    // Manipulator dimensions
    this.sphere_radius = viewSize / 5.0;
    this.axis_vector_size = this.sphere_radius;

    // Manipulator materials
    this.sphere_material = new THREE.MeshBasicMaterial({ color: 0x606565, transparent: true, opacity: 0.2, depthTest: false, depthWrite:false });
    this.axis_rod_material = new THREE.MeshBasicMaterial({ color: 0x500000 });
    this.axis_tip_material = new THREE.MeshBasicMaterial({ color: 0x5050FF });

    var rod_radius = this.axis_vector_size / 40.0;
    var tip_radius = rod_radius * 2.0;

    // Manipulator geometries
    this.sphere_geometry = new THREE.SphereGeometry(this.sphere_radius, 32, 32);
    this.axis_rod_geometry = new THREE.CylinderGeometry(rod_radius, rod_radius, this.axis_vector_size, 32, 1, true);
    this.axis_tip_geometry = new THREE.SphereGeometry(tip_radius, 32, 32);

    geom.rotate_ip(v3.Axes.X, this.axis_rod_geometry);

    // Geometry names
    this.sphere_geometry.name = "edge_tangent_manip_sphere_"+i;
    this.axis_rod_geometry.name = "edge_tangent_manip_vector_rod_"+i;
    this.axis_tip_geometry.name = "edge_tangent_manip_vector_tip_"+i;

    this.axis_rod_geometry.translate(
      (this.axis_vector_size * this.local_x_direction.x) / 2,
      (this.axis_vector_size * this.local_x_direction.y) / 2,
      (this.axis_vector_size * this.local_x_direction.z) / 2
    );
    this.axis_tip_geometry.translate(
      this.axis_vector_size * this.local_x_direction.x,
      this.axis_vector_size * this.local_x_direction.y,
      this.axis_vector_size * this.local_x_direction.z
    );

    // Manipulator elements
    this.sphere = null;
    this.axis_rod = null;
    this.axis_tip = null;

    this.sphere = new THREE.Mesh(this.sphere_geometry, this.sphere_material);
    this.axis_rod = new THREE.Mesh(this.axis_rod_geometry, this.axis_rod_material);
    this.axis_tip = new THREE.Mesh(this.axis_tip_geometry, this.axis_tip_material);

    // Flag for shown/not shown
    this.is_shown = false;

    // The edge and vertex object that the manipulator is applied to
    this.edge_object_id = edge_object.id;
    let vertex_ids = uil_get_vertices_of_edge(edge_object.id);
    this.vertex_object_id = vertex_ids[i-1];

    // Add to respective scene nodes
    uid_manip_aux_raynode.add(this.sphere);
    uid_manip_aux_raynode.add(this.axis_rod);
    uid_manip_raynode.add(this.axis_tip);
  }

  display () {
    var vertex_object = uid_vertices.getItem(this.vertex_object_id);
    var edge_object = uid_edges.getItem(this.edge_object_id);
    this.attach_to_vertex_of_edge(vertex_object, edge_object);
    this.is_shown = true;
  }

  remove () {
    uid_manip_aux_raynode.remove(this.sphere);
    uid_manip_aux_raynode.remove(this.axis_rod);
    uid_manip_raynode.remove(this.axis_tip);
    this.sphere = null;
    this.axis_rod = null;
    this.axis_tip = null;
    this.is_shown = false;
  }

  attach_to_vertex_of_edge (vertex_object, edge_object) {
    var tangent = uil_get_edge_tangent_at_vertex(edge_object.id, vertex_object.id);
    var center = new THREE.Vector3(
      vertex_object.object3d.position.x,
      vertex_object.object3d.position.y,
      vertex_object.object3d.position.z
    );

    this.match_to_vectors(center, tangent);
  }

  match_to_vectors ( center, tangent ) {
    // Takes a vertex normal manipulator and transforms it so that it matches the vectors (center, normal).

    var nv = new THREE.Vector3(tangent.x, tangent.y, tangent.z).normalize();
    var R = m3.rotationMatrixBetweenVectors(this.local_x_direction, nv);

    // Rotate manipulator
    this.rotate(R);

    // Set manipulator description geometry
    this.local_x_direction.x = nv.x;
    this.local_x_direction.y = nv.y;
    this.local_x_direction.z = nv.z;

    // Translate manipulator
    var t = new THREE.Vector3();
    t.subVectors(center, this.center);
    this.translate(t);
  }

  rotate ( R ) {
    // Rotate manipulator by matrix "R"

    // Rotate main description vectors.
    this.local_x_direction.applyMatrix3(R);

    // Rotate geometries.
    var m4 = new THREE.Matrix4();
    m4.set(
      R.elements[0],
      R.elements[3],
      R.elements[6],
      0,
      R.elements[1],
      R.elements[4],
      R.elements[7],
      0,
      R.elements[2],
      R.elements[5],
      R.elements[8],
      0,
      0,
      0,
      0,
      1
    );

    this.axis_rod_geometry.applyMatrix4(m4);
    this.axis_tip_geometry.applyMatrix4(m4);
  }

  translate ( t ) {
    // Translate manipulator by vector "t"

    // Translate main description vector
    this.center.addVectors(this.center, t);

    // Translate geometries
    this.sphere_geometry.translate(t.x, t.y, t.z);
    this.axis_rod_geometry.translate(t.x, t.y, t.z);
    this.axis_tip_geometry.translate(t.x, t.y, t.z);
  }

  rotate_around_axis ( center, axis, angle ) {
    let R = m3.vectorRotation(axis, angle);
    if (isNaN(R.elements[0])){
      //console_log(R);
    }

    let tocenter = new THREE.Vector3();
    let andback = new THREE.Vector3();
    tocenter.copy(center).multiplyScalar(-1);
    andback.copy(center);

    // Translate to origin
    this.translate(tocenter);

    // Rotate
    this.rotate(R);

    // Translate back
    this.translate(andback);
  }
}

window.fileFloor = false;
window.fileFront = false;
window.fileRight = false;
window.fileLeft = false;

window.leftUrl = "";
window.rightUrl = "";
window.frontUrl = "";
window.floorUrl = "";

// Dom_text ------------------------------------------------------------------


// A class for describing text that will be displayed in a DOM element
export class Dom_text {
  constructor(text_type, object_id, dom_id) {
    this.text_type = text_type;
    this.object_id = object_id;
    this.dom_id = dom_id;
    this.dist_from_camera = -1;
    this.coords = null;
  }

  get_value() {
    if (this.text_type == "tangent_edge") {
      // Find any tangencies on this edge.
      var tangencies = "T";
      var current_edge_id = this.object_id;
      var vids = uil_get_vertices_of_edge(current_edge_id);
      vids.forEach(function(vid) {
        var eids = uil_get_edges_of_vertex(vid);
        eids.forEach(function(eid){
          if (uil_is_joint_smooth(eid, current_edge_id) == 1) {
            var str;
            if (current_edge_id <= eid)
              var s = current_edge_id + "-" + eid;
            else
              var s = eid + "-" + current_edge_id;
            var str = dom_edge_tangency_strings.getItem(s) + ",";
            tangencies += str;
          }
        });
      });
      tangencies = tangencies.slice(0, -1);
      return tangencies;
    }
    else if (this.text_type == "dragged_edge_coordinates") {
      if (App.state.dragging_type == "edge" && uid_drag_object && uid_drag_object.type == "edge") {
        uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
        var wv_parameter = new WrappedValue();
        var intersection_point = uil_get_ray_edge_intersection(uid_drag_object.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction, false, wv_parameter);

        var vx = get_display_unit_from_unitless_value(intersection_point.x);
        var vy = get_display_unit_from_unitless_value(intersection_point.y);
        var vz = get_display_unit_from_unitless_value(intersection_point.z);

        var val = "(" + vx.toFixed(App.settings.coordinate_decimals) + ", " +
                        vy.toFixed(App.settings.coordinate_decimals) + ", " +
                        vz.toFixed(App.settings.coordinate_decimals) + ")";
        return val;
      }
      else {
        return "";
      }
    }
  }

  add_text_to_dom() {
    // If canvas has not been rendered yet, don't display text.
    if (App.state.canvas.width == 0 || !uid_camera)
      return;

    // If the item is already there don't add it again.
    if (dom_text_elements.hasItem(this.dom_id))
      return;

    var v = this.get_value();
    var class_text = "";

    var str = "<div class='no-copy " + class_text + "' id='" + this.dom_id + "' style='position: absolute'> <p class='" + class_text + "'>" + v + "</p> </div>";
    $( "#phi" ).append(str);
    this.reposition_text();
    dom_text_elements.setItem(this.dom_id, this);
  }

  remove_text_from_dom() {
    var str = "#" + this.dom_id;
    $(str).remove();
    dom_text_elements.removeItem(this.dom_id);
  }

  get_text_coordinates() {
    var coords;
    if (!uid_camera) {
      coords = new THREE.Vector2(0,0);
    }
    if (this.text_type == "tangent_edge") {
      var p = uil_get_point_at_edge_param(this.object_id, 0.5);
      this.dist_from_camera = v3.point_point_distance(uid_camera.position, p);
      coords = from_normalized_to_actual_screen_coordinates(from_3d_coordinates_to_2d_screen_coordinates(p));
    }
    else if (this.text_type == "dragged_edge_coordinates") {
      this.dist_from_camera = 0;
      coords = from_normalized_to_actual_screen_coordinates(pointer_normalised);
    }
    else if (this.text_type == "filename") {
      this.dist_from_camera = 0;
      coords = new THREE.Vector2();
      coords.x = xOffset + 20;
      coords.y = App.state.canvas.height + yOffset - 20;
    }
    // Check that we're not superimposing on some other text. If so, keep only the text that is closest to the camera
    var textwith = 50;
    var textheight = 15;
    var current_dte = this;
    dom_text_elements.values().forEach(function(dte) {
      if (dte.object_id != current_dte.object_id && dte.dist_from_camera != -1 &&
          dte.dist_from_camera < current_dte.dist_from_camera &&
          Math.abs(dte.coords.x - coords.x) < textwith && Math.abs(dte.coords.y - coords.y) < textheight) {
        coords.set(-1,-1);
        current_dte.dist_from_camera = -1;
      }
    });

    this.coords = coords;
    return coords;
  }

  update_text_value() {
    var v = this.get_value();
    var str = "#" + this.dom_id + " > p";
    $(str).html(v);
  }

  remove_text() {
    var str = "#" + this.dom_id + " > p";
    $(str).html("");
  }

  reposition_text() {
    var p = this.get_text_coordinates();

    // If coordinates are dodgy, remove the text.
    if (p.x == 0 && p.y == 0)
      this.remove_text();

    var c = document.getElementById("scene-container");
    var txt_rect = c.getBoundingClientRect();

    if (p.x < txt_rect.right && p.x > txt_rect.left && p.y < txt_rect.bottom && p.y > txt_rect.top) {
      this.update_text_value();
    }
    else {
      this.remove_text();
    }

    p.x = Math.min(p.x, Math.floor(txt_rect.right));
    p.x = Math.max(p.x, Math.floor(txt_rect.left));
    p.y = Math.min(p.y, Math.floor(txt_rect.bottom));
    p.y = Math.max(p.y, Math.floor(txt_rect.top));

    var str = "#" + this.dom_id;
    $(str).css({top: p.y+0, left: p.x+0, position:'absolute'});
  }
}

window.global_edge_projection_data = undefined

window.global_default_model_data = undefined

// ----------------------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------------------
// ----------------------        G L O B A L   V A R I A B L E S        -------------------------------
// ----------------------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------------------

// User interaction state
window.uid_state = null;

// Global flag for reading/writing extra data from/to Phi files
window.use_phi_file_extra_data = true;

// Current API call and related information
window.uid_current_api_call = "";
window.uid_api_add_vertex_new_edge1_id = 0;
window.uid_api_add_vertex_new_edge2_id = 0;
window.uid_api_add_edge_new_face1_id = 0;
window.uid_api_add_edge_new_face2_id = 0;
window.uid_api_delete_vertex_new_edge_id = 0;
window.uid_api_pop_faces = [];
window.pop_amount_value = 0;

window.default_file_name = "Untitled";

// For adding edges and then inflating/deflating faces;
window.uid_add_edge_face_normal_before = undefined
window.uid_add_edge_face_sample_points_before = undefined

// Flag for whether we're in a compound operation
window.uid_in_compound_operation = false;

// Flag for whether we're in a ui-side compound operation
window.currently_in_ui_side_compound_operation = false;

// Global variable logging all function call descriptions
window.cfcd = undefined

// Set of triangulated faces for STL output
// Hash with STL face triangles. Face id is key, THREE.geometry object containing arrays of vertices and triangles is the
// value.
window.stl_surfaces_hash = new HashTable();
window.last_imported_stl_units = "cm";

// A global ID generator
window.uid_global_id = 1;

// The size of the ball used when deforming curves and
// the hardness of the ball used when deforming curves
window.uid_ball_size = 1.0;
window.uid_default_ball_size = 1.6;
window.uid_ball_hardness = 0.0;

// Smoothen joint default radii and strength
window.smoothen_joint_radius = 0.5;
export const FAINT_STRENGTH = 0.01;
export const SOFT_STRENGTH = 0.25;
export const REGULAR_STRENGTH = 0.5;
export const FIRM_STRENGTH = 0.75;
export const STRONG_STRENGTH = 1;

window.global_smoothen_strength = REGULAR_STRENGTH;

// The factor we use to display curvature coloring.
window.curvature_coloring_factor = 1.0;

// The face objects being scaled, either a face or a set of edges. Note that a face translates to a set of edges.
window.scale_face_object = null;
window.scale_edge_objects = null;
// The vector for the center of the scaling:
window.scale_center = null;
// The factor by which we scale
window.scale_factor = 1.0;

// The single object or set of objects currently being dragged, depending on drag type. These variables refer to
// (sets of) JS objects, not IDs.
window.uid_drag_object = undefined
window.uid_additional_drag_vertices = [];
window.uid_additional_drag_vertex_origin_offsets = [];
window.uid_drag_edge_for_vertex = undefined

// A state flag about whether we are currently in drag mode or not.
window.uid_in_dragging_mode = false;

// A state of model consistency issues
window.model_consistency_problems = false;
window.remove_all_glows_timeout_handle = null;
window.consistency_problem_edge_list = [];
window.consistency_problem_face_list = [];
window.consistency_problem_stretch_factor = 5;
window.consistency_problem_min_length_factor = 300;
window.consistency_problem_min_curv_radius_factor = 10;

// Manipulators possibly present in scene
window.uid_rigid_move_manipulator = null;
window.uid_edge_tangent_1st_manipulator = null;
window.uid_edge_tangent_2nd_manipulator = null;

// Keep track of manipulator vectors for reattaching them
window.tmp_rigid_move_manipulator_center = null;
window.tmp_rigid_move_manipulator_axis = null;
window.tmp_rigid_move_manipulator_normal = null;

// The currently working manipulator
window.uid_selected_manipulator = undefined
window.uid_selected_manipulator_type = "";

// Mouse locations to track manipulator movement.
window.uid_manip_previous_raycaster_origin = undefined
window.uid_manip_current_raycaster_origin = undefined
window.uid_manip_previous_raycaster_direction = undefined
window.uid_manip_current_raycaster_direction = undefined

window.uid_snap_delta = undefined

// Hash tables containing all currently selected edges, vertices and faces
window.uid_selected_edges = undefined
window.uid_selected_vertices = undefined
window.uid_selected_faces = undefined

// Hash table showing the location where a selected edge was clicked.
window.uid_selected_edges_click_coordinates = undefined

// Contains the closest of the two vertices of the last selected edge to the edge
// click location.
window.uid_selected_edge_click_point = undefined

// Array containing currently selected object IDs.
window.currently_selected_objects = [];

// Selection box
window.uid_selection_box = undefined

window.must_redraw_sketches = false;

// Hash containing all reference vertices.
window.uid_reference_vertices = undefined
// Hash used to quickly check whether a reference vertex clashes with another one
window.uid_reference_vertex_coords_hash = undefined
// Hash containing all reference faces.
window.uid_reference_faces = undefined
// Hash containing all reference edges.
window.uid_reference_edges = undefined
// Hash containing all reference bodies
window.uid_reference_bodies = undefined

// Hash containing all vertices with undeclared tangencies
window.uid_vertices_with_undeclared_tangencies = undefined

window.uid_selected_reference_faces = [];

// Two hashes for a reference body lattice.
// var uid_reference_face_edges_onshape_ids;
// var uid_reference_edge_vertices_onshape_ids;

// Objects to help with attaching to reference model
window.all_ref_points = null;
window.all_ref_triangles = null;
window.stl_triplets_hash = null;
window.vertices_to_attach_move_directions = null;
window.reference_object_for_attaching_surface = null;
window.uid_ref_faces_for_attaching_surface_raynode = null;

// The currently transiently highlighted object3D. This is highlighting caused by mouse hovering over them.
window.uid_highlighted_object_o3d = undefined

// Diameter of vertex point
window.uid_vertex_diameter = 0.2;
window.vertex_scaling_factor = 3.5;

// Display data structure list of vertices, edges and faces. They are Hash tables indexed
// by the ui_ids of the objects.
window.uid_vertices = undefined
window.uid_edges = undefined
window.uid_faces = undefined
window.uid_bodies = undefined
window.uid_materials_hash = undefined
window.uid_rotmanip_center = undefined
window.uid_rigid_move_manip_center = undefined
window.uid_rigid_move_manip_normal_vector_direction = undefined
window.uid_dragging_raycaster_previous_origin = undefined
window.uid_last_used_face_material_name = undefined

// Two extra hashes to hold colors and materials for faces that may have been deleted and re-created

window.uid_face_colors = undefined
window.uid_face_materials = undefined

// A global bounding box dimension
window.uid_global_bb = undefined

// Type of face rendering: solid, transparent
window.uid_face_rendering_type = "solid";
window.uid_face_rendering_type_before_move_manipulator = "solid";

// Default face rendering material
window.uid_default_face_material_name = undefined
window.uid_last_used_face_material = null;

// Current callback face material and color
window.uid_callback_face_material = null;
window.uid_callback_face_color = null;

// Hash table associating the object3d ID of each object with the uid_object itself.
window.uid_object3dIDToUid_object = undefined

// XXX DEBUG GLOBAL VARIABLE.
// If 0, draws points and triangles on surface AFTER the first drag is complete.
// If 1, draws points and triangles on surface on load.
window.drawpoints = 0;

// Array of faces, vertices and edges that have been singled out for display.
window.singled_out_faces_to_hide = [];
window.singled_out_vertices_to_hide = [];
window.singled_out_edges_to_hide = [];

// The ID of the inward and outward facing face so we can subsequently pop-it-out.
window.inward_looking_converted_face_id = 0;
window.outward_looking_converted_face_id = 0;
window.make_sandwich_converted_face = false;

// A flag for whether we want crosshair to go on 1/8th of edge length
window.put_crosshair_at_edge_eighths = true;
window.crosshair_edge_fraction = 0.25;

// Hash of arrays for showing curvatures
window.face_color_hash = undefined

// Hash of arrays of vectors for showing isophotes
window.face_normals_hash = undefined

// Debug variable for displaying curvatures in exaggerated way
window.curvature_debug_flag = false;

window.workspace_unit = undefined
window.workspace_unit_coordinate_factor = undefined
window.partstudio_unit_coordinate_factor = 0.0254; // Because the STL API export uses inches as units

// Flag to indicate whether reference STL vertices should be loaded.
window.do_not_load_reference_vertices = false;

// the symmetry plane base and normal of the selected symmetry plane
window.mirror_plane_base = new THREE.Vector3();
window.mirror_plane_normal = new THREE.Vector3();

// For temporariry hiding mirror plane to carve etc
window.temporarily_hiding_mirror_side = false;
window.temporary_mirror_plane_base = null;
window.temporary_mirror_plane_normal = null;

window.symmetry_plane_base = new THREE.Vector3();
window.symmetry_plane_normal = new THREE.Vector3();
window.symmetry_edges_list_first = new Array();
window.symmetry_edges_list_second = new Array();

// Edge perpendicular plane
window.edge_perpendicular_plane_selected = false;
window.edge_perpendicular_plane_base = new THREE.Vector3();
window.edge_perpendicular_plane_normal = new THREE.Vector3();

// Circularity global variables
window.global_exact_circle_center_vertex_id = 0;
window.global_exact_circle_edge_ids = [];
window.global_exact_circle_single_edge_vertex_to_remove = 0;
window.global_exact_circle_vertices = [];
window.global_exact_circle_fixed_vertices = [];
window.global_exact_circle_radius = 0;
window.global_exact_circle_normal = null;
window.global_exact_circle_center_coordinates = null;
window.global_exact_circle_is_arc = false;

// Colors
window.color_r_vertex = 130;
window.color_g_vertex = 172;
window.color_b_vertex = 255;

window.color_r_face = Math.floor(220);
window.color_g_face = Math.floor(230);
window.color_b_face = Math.floor(250);

window.color_consistency_problem_r = 255;
window.color_consistency_problem_g = 51;
window.color_consistency_problem_b = 0;

window.color_consistency_problem_face_r = 255;
window.color_consistency_problem_face_g = 188;
window.color_consistency_problem_face_b = 0;

window.color_sketch_edge_r = 51;
window.color_sketch_edge_g = 150;
window.color_sketch_edge_b = 150;
window.color_sketch_vertex_r = 51;
window.color_sketch_vertex_g = 150;
window.color_sketch_vertex_b = 150;

window.color_sketch_edge_soft_r = 51;
window.color_sketch_edge_soft_g = 150;
window.color_sketch_edge_soft_b = 150;
window.color_sketch_vertex_soft_r = 51;
window.color_sketch_vertex_soft_g = 150;
window.color_sketch_vertex_soft_b = 150;

window.color_undeclared_vertex_tangency_r = 255;
window.color_undeclared_vertex_tangency_g = 100;
window.color_undeclared_vertex_tangency_b = 0;

window.color_r_edge = 130;
window.color_g_edge = 172;
window.color_b_edge = 255;

window.color_linear_edge_r = Math.floor(color_r_edge) * 1.4;
window.color_linear_edge_g = Math.floor(color_g_edge) * 1.4;
window.color_linear_edge_b = Math.floor(color_b_edge);

window.color_fixed_r = Math.floor(0.5*256);
window.color_fixed_g = Math.floor(0.5*256);
window.color_fixed_b = Math.floor(0.5*256);

window.uid_highlight_color_percent = 0.6;

window.color_face_working_r = Math.floor(0.5*256);
window.color_face_working_g = Math.floor(0.9*256);
window.color_face_working_b = Math.floor(0.9*256);

window.color_ref_face_selected_r = 255;
window.color_ref_face_selected_g = 180;
window.color_ref_face_selected_b = 0;

window.default_line_width = 0.8;
window.thick_line_width = 2.5;

window.ref_stl_face_color_r = 51;
window.ref_stl_face_color_g = 205;
window.ref_stl_face_color_b = 102;

window.ref_parasolid_face_color_r = 205;
window.ref_parasolid_face_color_g = 51;
window.ref_parasolid_face_color_b = 102;

window.ref_partstudio_face_color_r = 51;
window.ref_partstudio_face_color_g = 102;
window.ref_partstudio_face_color_b = 205;

window.ref_solid_face_color_r = 200;
window.ref_solid_face_color_g = 190;
window.ref_solid_face_color_b = 120;

// Grid colors
window.color_grid_red = 0xbb3333;
window.color_grid_green = 0x33bb33;
window.color_grid_blue = 0x3333bb;
window.color_grid_gray = 0x666666;

// Time logging variables. We define 3 counters A, B, C.
window.tA0 = performance.now();
window.tA1 = performance.now();
window.tB0 = performance.now();
window.tB1 = performance.now();
window.tC0 = performance.now();
window.tC1 = performance.now();

// Mouse sensitivity in pixels to hit point:
window.sensitivity = 5;

window.stats = undefined
window.renderer = undefined
window.composer = undefined

// Where is the camera looking
window.viewdir = undefined
window.viewup = undefined
window.rect = undefined
window.xOffset = undefined
window.yOffset = undefined

window.viewSize = undefined

window.aspectRatio = 0;
window.viewToWorld = undefined

window.scene = undefined

window.range_ball_size_slider = undefined

// Camera and camera controls
window.uid_camera = undefined

// Camera location on click
window.uid_camera_click_origin = null;
window.uid_camera_click_direction = null;
window.uid_camera_click_up = null;
window.uid_linear_draw_direction = null;

// Raycaster
window.uid_raycaster = undefined

// The line precision as a fraction of the viewSize.
window.uid_raycaster_line_precision_factor = 120;

// This is the raynode under which the manipulators are attached.
window.uid_manip_raynode = undefined

// This is the raynode under which the objects are attached.
window.uid_objects_raynode = undefined

// An extra node for the visible (non-interactive) edge mesh
window.uid_visible_edge_mesh_raynode = undefined

// This is the raynode under which reference faces are attached.
window.uid_ref_partstudio_faces_raynode = undefined
window.uid_ref_stl_faces_raynode = undefined

// This is the raynode under which reference edges are attached.
window.uid_ref_partstudio_edges_raynode = undefined
window.uid_ref_partstudio_edges_visible_raynode = undefined
window.uid_ref_stl_edges_raynode = undefined
window.uid_ref_stl_edges_visible_raynode = undefined

// This is the raynode under which reference vertex objects are attached.
window.uid_ref_partstudio_vertex_raynode = undefined
window.uid_ref_stl_vertex_raynode = undefined

// This is the raynode under which parasolid objects are attached.
window.uid_ref_parasolid_vertex_raynode = undefined
window.uid_ref_parasolid_faces_raynode = undefined
window.uid_ref_parasolid_edges_raynode = undefined
window.uid_ref_parasolid_edges_visible_raynode = undefined
window.uid_ref_parasolid_raynode = undefined

// Nodes to hold partstudio, stl and parasolid reference models.
window.uid_reference_partstudio_raynode = undefined
window.uid_reference_stl_raynode = undefined
window.uid_reference_parasolid_raynode = undefined

// This is the raynode used for raycasting. We make this point to either the objects or the manipulator, depending on usage.
window.uid_raynode = undefined

// A hierarchy of nodes under the scene
window.uid_geometries_raynode = undefined;
window.uid_reference_raynode = undefined;

// Auxiliary node for displaying things like non-interactive manipulator objects etc.
window.uid_manip_aux_raynode = undefined

// Auxiliary node for displaying a grid
window.uid_grid_raynode = undefined

// Auxiliary node for displaying working plane
window.uid_working_plane_raynode = undefined

// Auxiliary node for sketch planes, and for actual phi objects relevant too sketches
window.uid_sketch_plane_raynode = undefined
window.uid_deleted_sketch_plane_raynode = undefined
window.uid_sketch_objects_raynode = undefined
window.uid_hidden_sketch_plane_raynode = undefined
window.uid_hidden_sketch_objects_raynode = undefined
window.uid_temporary_node_for_sketch_raynode = undefined

// Auxiliary node for displaying tangencies, and flag for whether they are displayed
window.uid_tangent_display_raynode = undefined
window.tangencies_are_temporarily_hidden = false;
window.edges_for_which_to_display_tangency = undefined;

// Node for shadow plane
window.uid_shadow_plane_raynode = undefined

// The shadow plane and grid
window.shadow_plane_mesh = undefined
window.shadow_plane_grid_1 = undefined
window.shadow_plane_grid_2 = undefined

// The Z location of the shadow plane.
window.shadow_plane_z = undefined
window.shadow_plane_scale = undefined

// The shadow light
window.shadow_light = undefined

// The spotlight
// Spotlight. We need to keep the spotLight a global variable, because we'll be moving it around in other parts of the code.
window.global_spotLight = undefined;
// We also use a global static vector too calculate the spotlight new position, as this will be happening constantly.
window.global_static_vector = new THREE.Vector3();

// Auxiliary node displaying halo
window.uid_halo_raynode = undefined
//var uid_halo_direction;
//var uid_halo_position;
window.uid_halo_initial_vertical_2D_position = undefined
window.uid_halo_initial_horizontal_2D_position = undefined
window.uid_halo = null;

// Auxiliary node displaying selection box
window.uid_selection_box_raynode = undefined

// Auxiliary node for displaying text (coordinates) and the corresponding colors for the text.
window.uid_text_raynode = undefined
window.default_text_color_r = 0.5;
window.default_text_color_g = 0.5;
window.default_text_color_b = 0.5;

// Hashes of recently modified objects. One as picked up by the UI based on user's operations,
// and one based on the objects the core returns modified versions of.
window.user_modified_objects_hash = undefined
window.core_modified_objects_hash = undefined

// Hash of icon states
//var icon_state_hash;
// Hash for names of icons that could appear in context_specific_menu
//var icon_context_names_hash;

// Auxiliary node for displaying debug objects and relevant flag
window.uid_debug_obj_raynode = undefined
window.uid_debug_objs_visible = false;

// Additional scene nodes
window.uid_symmetry_plane_raynode = undefined
window.uid_mirror_plane_raynode = undefined
window.uid_edge_midpoint_raynode = undefined
window.uid_edge_midpoint_sections = 2;
window.uid_edge_midpoint_section_node_1 = undefined
window.uid_edge_midpoint_section_node_2 = undefined
window.uid_edge_midpoint_section_node_3 = undefined
window.uid_edge_midpoint_section_node_4 = undefined
window.uid_edge_midpoint_section_node_5 = undefined
window.uid_edge_midpoint_section_node_6 = undefined
window.uid_edge_midpoint_section_node_7 = undefined
window.uid_edge_midpoint_section_nodes = [];
window.uid_edge_midpoint_visible = false;
window.uid_clipping_planes_raynode = undefined

// Node for the lights
window.uid_lights_raynode = undefined

// Node for the 3 orthogonal planes (front, top, side)
window.uid_axes_raynode = undefined
window.uid_axes_planes_raynode = undefined

// The origin of the grid
window.uid_grid_origin = undefined

// The normal of the grid plane (a character x, y or z)
window.uid_grid_normal = undefined

// The grid step, min and max (dimension of each grid box and start/end of grid dimensions)
window.uid_grid_step = undefined
window.uid_grid_min = undefined
window.uid_grid_max = undefined
window.currently_selected_grid_button = "C";

// Flag indicating whether we're snapping to grid.
window.snap_to_grid = true;
window.snapping_is_selected = false;

// Auxiliary node for displaying ball size disk.
window.uid_aux_ball_disk_raynode = undefined

// Auxiliary node for displaying draw/carve artifacts.
window.uid_draw_edge_raynode = undefined

// Auxiliary node for displaying vertex glows
window.uid_glow_raynode = undefined

// Auxiliary node for displaying rotation center
window.uid_rotation_center_raynode = undefined;

// For ball size
window.circle = undefined

// For timeouts
window.timeoutvar = undefined

// For keeping track of 0-thickness faces
window.zero_thickness_faces_hash = null;
window.zero_thickness_edge_list_to_check = [];

// For adding vertices or edges
window.addEdge_first_vertex_object_id = -100;
window.addEdge_first_vertex_edge_object_id = undefined
window.addEdge_second_vertex_object_id = undefined
window.addEdge_first_vertex_ray_origin = new THREE.Vector3();
window.addEdge_first_vertex_ray_direction = new THREE.Vector3();

// For drawing edges on faces
window.addEdge_draw_raycasting_origins = undefined
window.addEdge_draw_raycasting_direction = undefined
window.addEdge_draw_intersected_vertices = undefined
window.addEdge_draw_intersected_edges = undefined
window.addEdge_draw_intersected_faces = undefined
window.addEdge_draw_raycasting_new_vertex_origins = undefined

// For offsetting face
window.offset_face_object = undefined
window.offset_face_plane_base = new THREE.Vector3();
window.offset_face_plane_normal = new THREE.Vector3();
window.offset_face_vertex = null;
window.offset_face_vertices = [];
window.offset_point_coordinates = null;
window.offset_face_color_r = undefined
window.offset_face_color_g = undefined
window.offset_face_color_b = undefined

// For downloading file
window.textFile = null;

// Axis plane geometries
window.top_text_geometry = null;
window.front_text_geometry = null;
window.side_text_geometry = null;

// Code state flags
window.ENABLE_INFLATE_DEFLATE = false;
window.CONSOLE_LOGGING = false;
window.ENABLE_DEBUG_TOOLS = false;

window.floorX = undefined
window.floorY = undefined
window.floorZ = undefined // The checkered floor object
window.floor_visible = undefined

window.vertices_and_edges_visible = true;
// For temporarily storing state:
window.hidden_edges_currently_visible = false;

window.original_view_size = undefined
window.current_zoom = 1;
window.zoom_manipulator_timeout_handle = null;
window.post_zoom_actions_timeout_handle = null;
window.must_show_manipulator_after_zoom = false;
window.must_show_axes_after_zoom = false;
window.must_show_vertices_after_zoom = false;

// A copy of the shift_is_pressed flag for while drawing on face, to make sure it's still available when processing later on.
window.shift_was_pressed_during_adding_edge = false;

// A copy of the hidden edges flag for while drawing on face, to make sure it's still available when processing later on.
window.hidden_edges_were_visible_before_drawing_edge = false;

// View control
window.current_view = "home"; //x, y, z, home
window.home_viewdir = new THREE.Vector3();
window.home_viewup = new THREE.Vector3();
window.home_look_vector = new THREE.Vector3();
window.home_position = new THREE.Vector3();
window.viewing_down_orthogonal_plane = false;
window.last_viewed_down_positive_axis = false;

// Dragging orthogonally
window.drag_start_ray_origin = new THREE.Vector3();
window.drag_start_hit_point = new THREE.Vector3();
window.drag_start_movement_direction = "none";
window.last_intersection_coordinates = new THREE.Vector3();

// Plane for working within a plane (e.g. dragging vertex within plane) and flag
// denoting whether a working plane is currently set.
window.working_plane_base = new THREE.Vector3();
window.working_plane_normal = new THREE.Vector3();
window.working_plane_set = false;

// Flag about whether we should be loading the new model into the current one or not.
window.loading_into_current_model = false;

// Dictionary of debug variables
window.debug_vars = undefined

// List of shortcuts
window.shortcuts = undefined

// The server domain name
window.static_page_url = "https://www.phi3d.com/";

// Save compression level for non-autosave
window.save_compression_level = 6;

// Magic numbers
window.magic_numbers_str = "";

//------------------------

// Variables read from server input that define how system is to be initialied:
window.init_oh = 0;
window.init_did = "";
window.init_wid = "";
window.init_eid = "";
window.init_srv = "";
window.init_oat = "";
window.init_ort = "";
window.init_oabf = 1;

window.file_has_edu_license = false;

// Onshape-related variables
window.onshape_document_id = "";
window.onshape_workspace_id = "";
window.onshape_element_id = "";

// Tutorial stage
window.max_tut_stage = 13;

// Which operation is causing the Onshape selectItemDialog to send us an element
window.onshape_selectItemDialog_usage = "";

// Face appearance
window.normalMap = undefined
window.aoMap = undefined
window.displacementMap = undefined

// Hashtable of dom text elements, and specific dom text elements.
window.dom_text_elements = undefined

// Hashtable for edge tangency string reference
window.dom_edge_tangency_strings = undefined

// Face reflected textures
window.reflection_urls = undefined

// ----------------------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------------------
// ----------------------        I M A G E   P A T H S        -----------------------------------------
// ----------------------------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------------------------

window.car_front_png = "img/Static/car_front.png";
window.car_left_png = "img/Static/car_left.png";
window.car_right_png = "img/Static/car_right.png";
window.car_floor_png = "img/Static/car_floor.png";
window.boat_front_png = "img/Static/boat_front.png";
window.boat_left_png = "img/Static/boat_left.png";
window.boat_right_png = "img/Static/boat_right.png";
window.boat_tfloor_png = "img/Static/boat_floor.png";

/* ------------------ */

window.img_upload_hover_icon_png = "img/upload_hover_icon.png";
window.img_upload_icon_png = "img/upload_icon.png";
window.img_open_icon_png = "img/open_icon.png";
window.img_save_icon_png = "img/save_icon.png";
window.img_export_hover_icon_png = "img/export_hover_icon.png";
window.img_export_icon_png = "img/export_icon.png";
window.img_import_hover_icon_png = "img/import_hover_icon.png";
window.img_import_icon_png = "img/import_icon.png";
window.img_xml_active_png = "img/xml_active.png";
window.img_xml_hover_png = "img/xml_hover.png";
window.img_do_magic_active_png = "img/do_magic_active.png";
window.img_do_magic_hover_png = "img/do_magic_hover.png";
window.img_vars_active_png = "img/vars_active.png";
window.img_vars_hover_png = "img/vars_hover.png";
window.img_stl_png = "img/stl.png";
window.img_onshape_logo_png = "img/onshape_logo.png";
window.img_step_png = "img/step.png";
window.img_stl_file_icon_png = "img/stl_file_icon.png";
window.img_3dprint_png = "img/3dprint.png";
window.img_3dscan_png = "img/3dscan.png";
window.img_phenoworld_png = "img/phenoworld.png";
window.img_phenoprint_png = "img/phenoprint.png";
window.img_upload_large_icon_png = "img/upload_large_icon.png";
window.img_save_file_png = "img/saveFile.png";
window.img_isomaterial_png = "img/isomaterial.png";
window.img_reflectioncastle_png = "img/reflectioncastle.png";
window.img_reflectioniso_png = "img/reflectioniso.png";
window.img_philogo_png = "img/philogoBeta.png";
window.img_phenometrylogo_png = "img/phenometrylogo.png";
window.img_tutorials_png = "img/starthere.jpg";
window.img_separator_png = "img/separator.png";

window.global_uniforms = {
  u_time: { type: 'f', value: 1.0 },
  colorB: { type: 'vec3', value: new THREE.Color(0xfff000) },
  colorA: { type: 'vec3', value: new THREE.Color(0xffffff) },
};
window.global_clock = new THREE.Clock();

window.core_entire_drag_modified_objects_hash = new HashTable();
